import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Loader } from 'components';
import { gridFilterTab, restoredGridState } from 'utils';

/** True when `state` holds at least one defined value — `{}` and `{ tabType: undefined }` do not. */
const hasRouterState = (state) => !!state && Object.values(state).some((value) => value !== undefined);

/**
 * Puts a user's saved filters back on a grid before that grid mounts.
 *
 * This has to happen during render, not in an effect. Every grid fetches from
 * `useEffect(..., [location.state])`, and effects run child-first — restoring from a
 * parent effect would let the grid fire one unfiltered request, paint the wrong rows,
 * then fire a second. Rendering a `<Navigate>` instead means the grid never mounts
 * until the state it reads is already correct: one request, no flash.
 *
 * It also blocks on the bootstrap read for the same reason. That read is issued once per
 * session from the MenuBar, so it is normally long settled; the only case that actually
 * waits is a hard refresh landing straight on a grid.
 *
 * Wrapping `<Routes>` rather than each route keys this off the pathname alone — a grid
 * added to `TAB_BY_PATHNAME` is covered without touching anything here.
 */
export const SavedFiltersGate = ({ children }) => {
    const location = useLocation();
    const { filters, loaded } = useSelector((state) => state.userPreferences);
    const tab = gridFilterTab(location?.pathname);

    // Not a grid that saves filters, or the user already carries state (they filtered,
    // paged, or arrived from a link that pre-selected something) — nothing to restore.
    // An empty state is not "carrying state": breadcrumbs navigate back with `{ ...saveParams }`,
    // which is `{}` for Clients/Staff — treating that as a choice dropped a cleared filter
    // back onto the grid's defaults.
    if (!tab || hasRouterState(location?.state)) return children;

    if (!loaded) return <Loader />;

    const restored = restoredGridState(tab, filters?.[tab]);
    if (Object.keys(restored).length) {
        return <Navigate to={location.pathname} state={restored} replace />;
    }

    return children;
};
